/**
 * <quick-add-pills>
 *
 * Wraps a group of quick-add option pills (size, gift card denominations,
 * or any other option configured in card-product.liquid) rendered by
 * snippets/card-product.liquid.
 * Clicking an available pill adds that variant to the cart directly —
 * there is no separate "Add to cart" button.
 *
 * Markup contract (see card-product.liquid):
 *  - Each pill is a <button data-quick-add-pill data-variant-id="...">
 *    Sold-out pills are rendered with the native `disabled` attribute
 *    and are never clickable.
 *  - A visually-hidden [data-quick-add-status] element (role="status",
 *    aria-live="polite") receives short text updates for screen reader
 *    users after each attempt.
 */
if (!customElements.get('quick-add-pills')) {
  class QuickAddPills extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
    }

    _onClick(event) {
      const button = event.target.closest('[data-quick-add-pill]');
      if (!button || !this.contains(button) || button.disabled) return;
      this._addToCart(button);
    }

    get _statusEl() {
      return this.querySelector('[data-quick-add-status]');
    }

    _announce(message) {
      const statusEl = this._statusEl;
      if (statusEl) statusEl.textContent = message;
    }

    async _addToCart(button) {
      const variantId = button.getAttribute('data-variant-id');
      const optionValue = button.getAttribute('data-option-value');
      const optionName = button.getAttribute('data-option-name');
      const productTitle = this.getAttribute('data-product-title') || '';

      if (!variantId) {
        this._announce(`Couldn't add ${productTitle} to cart. Please try again.`);
        return;
      }

      // Guard against double submission; also clears any leftover state class.
      button.classList.remove('quick-add-pill--added', 'quick-add-pill--error');
      button.classList.add('quick-add-pill--loading');
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;

      const itemLabel = optionValue
        ? `${productTitle}, ${optionName || 'option'} ${optionValue}`
        : productTitle;

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ id: variantId, quantity: 1, sections: ['cart-drawer', 'cart-icon-bubble'] }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.description || data?.message || 'Add to cart request failed');
        }

        // eslint-disable-next-line no-console
        console.log('[quick-add-pills] add-to-cart response:', data);

        button.classList.remove('quick-add-pill--loading');
        button.classList.add('quick-add-pill--added');
        this._announce(`Added ${itemLabel} to cart.`);

        this.dispatchEvent(
          new CustomEvent('quick-add:added', {
            bubbles: true,
            detail: { variantId, productTitle, optionName, optionValue, item: data },
          })
        );

        this._updateCartDrawer(data);
        this._refreshCartIconBubble();

        window.setTimeout(() => {
          button.classList.remove('quick-add-pill--added');
        }, 1500);
      } catch (error) {
        button.classList.add('quick-add-pill--error');
        this._announce(`Couldn't add ${itemLabel} to cart. Please try again.`);
        window.setTimeout(() => {
          button.classList.remove('quick-add-pill--error');
        }, 1500);
        // eslint-disable-next-line no-console
        console.error('[quick-add-pills]', error);
      } finally {
        button.classList.remove('quick-add-pill--loading');
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    }

    /**
     * Hands off to the theme's own CartDrawer.renderContents(), confirmed
     * from the actual cart-drawer.js source. It expects exactly the shape
     * /cart/add.js returns when you request `sections` — { id, sections }
     * — and internally: strips the drawer's `is-empty` class, swaps fresh
     * markup into #CartDrawer, and re-opens itself.
     *
     * Their getSectionsToRender() also expects a `cart-icon-bubble` entry.
     * That's not its own section in the header.liquid you shared, so if
     * Shopify doesn't return one, their internal loop can throw partway
     * through — but cart-drawer is processed first, so its content is
     * very likely already updated by the time that happens. Either way,
     * we fall back to a plain open() so the drawer still opens.
     */
    _updateCartDrawer(data) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;

      // This theme's cart-drawer.liquid puts "is-empty" on the <cart-drawer>
      // element itself (and on the nested <cart-drawer-items>), not on
      // .drawer__inner like the theme's own renderContents() assumes. Since
      // we just added an item, clear it directly on both so whatever CSS
      // keys off it stops showing the empty state, regardless of whether
      // renderContents() below fully succeeds.
      drawer.classList.remove('is-empty');
      const cartItems = drawer.querySelector('cart-drawer-items');
      if (cartItems) cartItems.classList.remove('is-empty');

      if (typeof drawer.renderContents === 'function') {
        try {
          drawer.renderContents(data);
          return;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[quick-add-pills] cart-drawer renderContents() failed:', error, data);
        }
      }

      if (typeof drawer.open === 'function') {
        try {
          drawer.open();
        } catch (error) {
          /* no-op */
        }
      }
    }

    /**
     * Fetches the fresh cart and dispatches a `cart:refresh` event (bubbling
     * to document) carrying the full cart JSON, so any other code can listen
     * for it. Also directly updates the header's #cart-icon-bubble count,
     * matching this theme's exact markup:
     *
     *   <a id="cart-icon-bubble">
     *     ...
     *     <div class="cart-count-bubble">
     *       <span aria-hidden="true">{{ cart.item_count }}</span>
     *       <span class="visually-hidden">...</span>
     *     </div>
     *   </a>
     *
     * Note: the visually-hidden count text is approximated in plain English
     * below rather than reproducing the theme's exact translated string
     * (`sections.header.cart_count`), since that wording isn't visible from
     * this file. Swap in the exact copy if it matters to you.
     */
    _refreshCartIconBubble() {
      fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
        .then((res) => (res.ok ? res.json() : null))
        .then((cart) => {
          if (!cart) return;

          document.dispatchEvent(
            new CustomEvent('cart:refresh', {
              bubbles: true,
              detail: { cart },
            })
          );

          this._updateCartBubble(cart);
        })
        .catch(() => {
          /* no-op: cart refresh is a bonus, not required for the add to succeed */
        });
    }

    _updateCartBubble(cart) {
      const cartIcon = document.getElementById('cart-icon-bubble');
      if (!cartIcon) return;

      let bubble = cartIcon.querySelector('.cart-count-bubble');

      if (cart.item_count <= 0) {
        if (bubble) bubble.remove();
        return;
      }

      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        cartIcon.appendChild(bubble);
      }

      bubble.innerHTML = '';

      if (cart.item_count < 100) {
        const count = document.createElement('span');
        count.setAttribute('aria-hidden', 'true');
        count.textContent = cart.item_count;
        bubble.appendChild(count);
      }

      const hiddenText = document.createElement('span');
      hiddenText.className = 'visually-hidden';
      hiddenText.textContent =
        cart.item_count === 1 ? '1 item in cart' : `${cart.item_count} items in cart`;
      bubble.appendChild(hiddenText);
    }
  }

  customElements.define('quick-add-pills', QuickAddPills);
}
